import React, { useCallback, useEffect, useState } from 'react';
import { healthsherpa } from './api';
import { PI_TARGETS, stripExport, targetLabel } from './hsCsv';
import { policyColors, policyTone } from './hsColors';
import { fs } from './hsScale';   // 5.31.0: font sizes scale with the page's Text size

// =============================================================================
// HealthSherpa phase 2 — Policy status (5.19.0). Lives on the HealthSherpa page (own tab, still hidden).
//   AlertsBox        one compact box of counts (Dashboard + top of the Clients tab); each line expands to a
//                    short list; 5.20.0: a click opens that PERSON's page (else the client in the InsureDocs tab).
//   UploadCard, RefreshCard, ExportsList   admin tools, shown on the Clients tab (5.20.0).
// 5.20.0: the "Link applications" review list is retired — the Clients tab (HealthSherpaClients.jsx) with
// its Assign button replaces it.
// Every value says where it came from and when (CT). Colours: hsColors.POLICY_TONES (formal, muted).
// =============================================================================

const detailOf = (e) => e?.response?.data?.detail || e?.message || 'Something went wrong';
export const usdCents = (c) => (c === null || c === undefined ? '—' : `$${(Number(c) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
export const fmtDay = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const pretty = (s) => String(s || '').replace(/_/g, ' ').replace(/^\w/, (x) => x.toUpperCase());

export function PolicyBadge({ value, darkMode, tone }) {
  if (!value) return <span>—</span>;
  const t = policyColors(tone || policyTone(value), darkMode);
  return (
    <span style={{ display: 'inline-block', background: t.bg, color: t.text, border: `1px solid ${t.border}`, borderRadius: 4,
      padding: '1px 8px', fontSize: fs(12), fontWeight: 600, whiteSpace: 'nowrap' }}>{pretty(value)}</span>
  );
}

const ALERT_LINES = [
  ['unpaid_binder', 'Unpaid first payment'],
  ['past_due', 'Past-due balance'],
  ['recent_end', 'Cancelled / terminated in the last 30 days'],
  ['other_aor', 'Another agent of record'],
  ['disagree', 'Sources disagree (export vs API)'],
  ['consent_expiring', 'Consents expiring within 60 days'],      // 5.40.0 (CMS consent by text)
  ['consent_expired', 'Consents expired'],
];

// 5.22.0: privacy (the Clients tab's Privacy button) blurs the names in the opened alert lists.
const PII_BLUR = { filter: 'blur(5px)', userSelect: 'none' };
export function AlertsBox({ c, onOpenClient, onOpenPerson, onNeedsLinking, reloadKey, privacy = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    let alive = true;
    healthsherpa.policy.summary().then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError(e?.response?.status === 403 ? 'hidden' : detailOf(e)); });
    return () => { alive = false; };
  }, [reloadKey]);
  const box = { background: c.panelBg, border: `1px solid ${c.borderColor}`, borderRadius: 8, padding: 14 };
  if (error === 'hidden') {
    return <div style={{ ...box, fontSize: fs(12), color: c.textSecondary }}>Policy status isn't open to staff yet.</div>;
  }
  if (error) return <div style={{ ...box, fontSize: fs(12), color: c.textSecondary }}>Policy status: {error}</div>;
  if (!data) return <div style={{ ...box, fontSize: fs(12), color: c.textSecondary }}>Loading policy status…</div>;
  const r = data.refresh || {};
  const danger = policyColors('danger', c.darkMode);
  const row = { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: `1px solid ${c.borderColor}`, fontSize: fs(13) };
  return (
    <div style={box} data-testid="hs-alerts">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
        <div style={{ fontSize: fs(13), fontWeight: 700 }}>Policy status &amp; alerts</div>
        <div style={{ fontSize: fs(11), color: c.textSecondary }}>plan year{data.plan_years.length > 1 ? 's' : ''} {data.plan_years.join(' + ')}</div>
      </div>
      {data.aor_check === false && (
        <div data-testid="hs-aor-check-off" style={{ fontSize: fs(12), color: c.textSecondary, padding: '4px 0' }}>
          Enter the Agent NPN to turn on agent-of-record checks.
        </div>
      )}
      {ALERT_LINES.map(([k, label]) => {
        const line = data.lines[k] || { count: 0, items: [] };
        const tone = line.count ? policyColors('attention', c.darkMode) : null;
        return (
          <div key={k}>
            <div style={{ ...row, cursor: line.count ? 'pointer' : 'default' }} onClick={() => line.count && setOpen(open === k ? null : k)}>
              <span style={{ minWidth: 34, textAlign: 'center', fontWeight: 700, borderRadius: 4, padding: '1px 6px',
                background: tone ? tone.bg : 'transparent', color: tone ? tone.text : c.textSecondary }}>{line.count}</span>
              <span style={{ flex: 1 }}>{label}</span>
              {line.count > 0 && <span style={{ fontSize: fs(11), color: c.textSecondary }}>{open === k ? '▾' : '▸'}</span>}
            </div>
            {open === k && (
              <div style={{ padding: '2px 0 8px 44px' }}>
                {line.items.map((i) => (
                  <div key={`${k}-${i.application_id}`} style={{ display: 'flex', gap: 10, fontSize: fs(12), padding: '2px 0', alignItems: 'baseline' }}>
                    {(i.person_id && onOpenPerson) || (i.client_id && onOpenClient)
                      ? <button onClick={() => (i.person_id && onOpenPerson ? onOpenPerson(i.person_id) : onOpenClient(i.client_id))} style={{ background: 'none', border: 'none', padding: 0, color: '#4a8fd1', cursor: 'pointer', fontSize: fs(12), textAlign: 'left' }}>
                        <span style={privacy ? PII_BLUR : undefined} data-pii="1">{i.name}</span>{!i.linked && <span style={{ color: c.textSecondary }}> (not assigned)</span>}</button>
                      : <span><span style={privacy ? PII_BLUR : undefined} data-pii="1">{i.name}</span>{!i.linked && <span style={{ color: c.textSecondary }}> (not assigned)</span>}</span>}
                    <span style={{ color: c.textSecondary }}>{i.carrier || ''}</span>
                    {i.amount_cents ? <span>{usdCents(i.amount_cents)}</span> : null}
                    {i.agent_of_record && <span style={{ color: c.textSecondary }}>{i.agent_of_record}</span>}
                    <span style={{ color: c.textSecondary, marginLeft: 'auto' }}>{i.since ? `since ${i.since}` : ''}</span>
                  </div>
                ))}
                {line.count > line.items.length && <div style={{ fontSize: fs(11), color: c.textSecondary }}>…and {line.count - line.items.length} more</div>}
              </div>
            )}
          </div>
        );
      })}
      <div style={row}>
        <span style={{ minWidth: 34, textAlign: 'center', fontWeight: 700, color: data.unassigned_people ? c.textPrimary : c.textSecondary }}>{data.unassigned_people ?? data.needs_linking}</span>
        <span style={{ flex: 1 }}>People not assigned to an InsureDocs client
          <span style={{ color: c.textSecondary }}> ({data.needs_linking} application{data.needs_linking === 1 ? '' : 's'})</span></span>
        {onNeedsLinking && data.needs_linking > 0 && (
          <button onClick={onNeedsLinking} style={{ fontSize: fs(11), background: 'none', border: `1px solid ${c.borderColor}`, color: c.textPrimary, borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>Show them</button>
        )}
      </div>
      <div style={{ ...row, ...(r.red ? { background: danger.bg, color: danger.text, borderRadius: 4, padding: '6px 8px', marginTop: 4 } : {}) }} data-testid="hs-last-refresh">
        <span style={{ flex: 1 }}>
          Last refresh: {r.last_run_label ? `${r.last_run_label} — ${r.last_result}` : 'none yet'}
          {r.red && !r.ran_today ? ` · this morning's ${r.schedule} run is missing` : ''}
          {r.red && r.last_result === 'failed' ? ' · the last run failed' : ''}
        </span>
        <span style={{ fontSize: fs(11), color: r.red ? danger.text : c.textSecondary }}>daily at {r.schedule}</span>
      </div>
    </div>
  );
}

// --- upload (admin) -----------------------------------------------------------------------------------

// 5.46.0: personal-info columns (Henry 2026-11-02) — a column that looks like a date of birth / tobacco / gender /
// household income or size and isn't confirmed yet shows the confirm step (names + what each maps to, never values).
export function UploadCard({ c, ui, onDone }) {
  const [file, setFile] = useState(null);
  const [text, setText] = useState(null);           // the file's text stays in this browser
  const [confirmed, setConfirmed] = useState(null);  // the saved mapping {column: target}
  const [parsed, setParsed] = useState(null);
  const [pending, setPending] = useState([]);        // [{name, target}] awaiting confirmation
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const loadMapping = async () => {
    const r = await healthsherpa.coverage.piColumns();
    const m = {};
    (r.data.columns || []).forEach((x) => { m[x.source_column] = x.target; });
    setConfirmed(m);
    return m;
  };
  const strip = (t, m) => {
    const p = stripExport(t, m);
    setParsed(p);
    setPending(p.personal.filter((x) => x.confirmed === null).map((x) => ({ name: x.name, target: x.detected || 'ignore' })));
  };
  const pick = (f) => {
    setFile(f); setParsed(null); setText(null); setPending([]); setError(null); setResult(null);
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const t = String(reader.result || '');
      try {
        const m = await loadMapping();
        setText(t);
        strip(t, m);
      } catch (e) { setError(e.message || detailOf(e)); }
    };
    reader.onerror = () => setError('Could not read the file');
    reader.readAsText(f);
  };
  const confirmColumns = async () => {
    setBusy(true); setError(null);
    try {
      await healthsherpa.coverage.confirmPiColumns(pending.map((x) => ({ source_column: x.name, target: x.target })));
      const m = await loadMapping();
      strip(text, m);
    } catch (e) { setError(detailOf(e)); }
    setBusy(false);
  };
  const upload = async () => {
    if (!parsed) return;
    setBusy(true); setError(null);
    try {
      const r = await healthsherpa.policy.uploadExport(file.name, parsed.rows);
      setResult(r.data); setParsed(null); setFile(null); setText(null);
      if (onDone) onDone();
    } catch (e) { setError(detailOf(e)); }
    setBusy(false);
  };
  return (
    <ui.Card c={c} title="Upload HealthSherpa export (admin)">
      <div style={{ fontSize: fs(12), color: c.textSecondary, marginBottom: 8 }}>
        The file is read in this browser: only the allowed columns are kept — the 53 policy columns, the household
        members' names, and the personal-info columns an admin confirmed (date of birth, tobacco, gender, household
        income / size — for quoting; masked in InsureDocs until someone clicks Show). SSNs are never kept. Nothing else
        leaves this PC. Re-uploading the same export updates its rows (no duplicates).
      </div>
      <input type="file" accept=".csv,.tsv,.txt,text/csv" onChange={(e) => pick(e.target.files && e.target.files[0])} data-testid="hs-export-file" />
      {parsed && pending.length > 0 && (
        <div style={{ marginTop: 10, fontSize: fs(13), border: `1px solid ${c.borderColor}`, borderRadius: 6, padding: 10 }} data-testid="hs-pi-confirm">
          <strong>New personal-info columns in this export — confirm what each one holds</strong>
          <div style={{ fontSize: fs(12), color: c.textSecondary, margin: '4px 0 8px' }}>
            Column names only (no values are shown). Confirmed columns are remembered for later uploads.
            {parsed.ssnColumns.length > 0 && ` ${parsed.ssnColumns.length} SSN column${parsed.ssnColumns.length === 1 ? ' is' : 's are'} always left out.`}
          </div>
          <table style={{ borderCollapse: 'collapse', fontSize: fs(13) }}>
            <thead><tr><th style={{ textAlign: 'left', padding: '2px 10px 2px 0' }}>Column in the file</th><th style={{ textAlign: 'left' }}>Keep as</th></tr></thead>
            <tbody>
              {pending.map((x, i) => (
                <tr key={x.name} data-testid="hs-pi-confirm-row" data-col={x.name}>
                  <td style={{ padding: '2px 10px 2px 0', fontFamily: 'monospace' }}>{x.name}</td>
                  <td>
                    <select value={x.target} aria-label={`Keep ${x.name} as`} data-testid="hs-pi-confirm-target"
                      onChange={(e) => setPending((ps) => ps.map((y, j) => (j === i ? { ...y, target: e.target.value } : y)))}>
                      {PI_TARGETS.map((t) => <option key={t} value={t}>{targetLabel(t)}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 8 }}>
            <button style={ui.btn(c, true, busy)} disabled={busy} onClick={confirmColumns} data-testid="hs-pi-confirm-save">
              {busy ? 'Saving…' : `Confirm ${pending.length} column${pending.length === 1 ? '' : 's'}`}</button>
          </div>
        </div>
      )}
      {parsed && pending.length === 0 && (
        <div style={{ marginTop: 10, fontSize: fs(13) }}>
          <strong>{parsed.rows.length.toLocaleString()} rows, {parsed.columns.length} columns — no SSNs</strong>
          {parsed.personalKept.length > 0 && <span data-testid="hs-pi-kept"> · personal info: {parsed.personalKept.map((n) => `${n} → ${targetLabel(confirmed && confirmed[n])}`).join(', ')}</span>}
          {parsed.dropped.length > 0 && <span style={{ color: c.textSecondary }}> · {parsed.dropped.length} other column{parsed.dropped.length === 1 ? '' : 's'} left out</span>}
          <div style={{ marginTop: 8 }}><button style={ui.btn(c, true, busy)} disabled={busy} onClick={upload} data-testid="hs-export-upload">{busy ? 'Uploading…' : 'Upload'}</button></div>
        </div>
      )}
      <ui.ErrorBox msg={error} />
      {result && (
        <div style={{ marginTop: 10, fontSize: fs(13) }}>
          ✓ Stored {result.rows} rows ({result.rows_added} added, {result.rows_updated} updated){result.rejected ? ` (${result.rejected} rejected)` : ''} · {result.applications_created} new applications ·
          auto-linked {result.linking.auto_phone} by phone, {result.linking.auto_email} by email, {result.linking.learned} learned
          {result.personal_info && <div data-testid="hs-pi-result">Personal info stored: {result.personal_info.dob} dates of birth, {result.personal_info.tobacco} tobacco, {result.personal_info.gender} gender, {result.personal_info.households} household values</div>}
          {/* 5.47.2: what couldn't be read (counts only — never a value) */}
          {result.personal_info && (() => {
            const pi = result.personal_info; const sk = pi.skipped || {};
            const total = Object.values(sk).reduce((a, b) => a + (b || 0), 0);
            const hid = pi.hidden || {}; const hidTotal = Object.values(hid).reduce((a, b) => a + (b || 0), 0);
            if (!total && !hidTotal && !pi.duplicate_members && !pi.skipped_rows) return null;
            const parts = [['dob', 'date of birth'], ['tobacco', 'tobacco'], ['gender', 'gender'], ['income', 'income'], ['size', 'household size']]
              .filter(([k]) => sk[k]).map(([k, w]) => `${sk[k]} ${w}`);
            return (
              <div data-testid="hs-pi-skipped" style={{ color: '#b45309' }}>
                {hidTotal > 0 && <span data-testid="hs-pi-hidden" style={{ color: 'inherit', opacity: 0.85 }}>{`${hidTotal} value${hidTotal === 1 ? ' was' : 's were'} hidden by HealthSherpa (${[['dob', 'date of birth'], ['income', 'income'], ['size', 'household size'], ['gender', 'gender'], ['tobacco', 'tobacco']].filter(([k]) => hid[k]).map(([k, w]) => `${hid[k]} ${w}`).join(', ')}) — stored values were kept; enter them on the client page or in Start enrollment. `}</span>}
                {total > 0 && `${total} value${total === 1 ? '' : 's'} couldn't be read and were left empty (${parts.join(', ')}). `}
                {pi.duplicate_members > 0 && `${pi.duplicate_members} member${pi.duplicate_members === 1 ? '' : 's'} listed twice in one row — kept once. `}
                {pi.skipped_rows > 0 && `${pi.skipped_rows} row${pi.skipped_rows === 1 ? '' : 's'} skipped (unreadable).`}
              </div>
            );
          })()}
        </div>
      )}
    </ui.Card>
  );
}

// --- refresh controls (admin) ------------------------------------------------------------------------

export function RefreshCard({ c, ui, planYears, onChanged }) {
  const [runs, setRuns] = useState([]);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const [past, setPast] = useState(String((Math.min(...(planYears || [new Date().getFullYear()]))) - 1));
  const load = useCallback(() => { healthsherpa.policy.runs().then((r) => setRuns(r.data.runs || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const all = async () => {
    setError(null);
    try { const r = await healthsherpa.policy.refreshAll(); setMsg(`Refresh started for ${r.data.plan_years.join(' + ')} — it takes a few minutes (about 1 call per second).`); setTimeout(load, 1500); } catch (e) { setError(detailOf(e)); }
  };
  const pastYear = async (force) => {
    setError(null);
    try { await healthsherpa.policy.refreshPastYear(Number(past), force); setMsg(`Plan year ${past}: refresh started (once, kept as history).`); setTimeout(load, 1500); } catch (e) { setError(detailOf(e)); }
  };
  return (
    <ui.Card c={c} title="HealthSherpa API refresh (admin)">
      <div style={{ fontSize: fs(12), color: c.textSecondary, marginBottom: 8 }}>Runs by itself every day at 8:30 AM CT. Past plan years are refreshed once and kept as history.</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button style={ui.btn(c, true)} onClick={all}>Refresh all now</button>
        <span style={{ width: 12 }} />
        <span style={{ fontSize: fs(12) }}>Past plan year</span>
        <input style={{ ...ui.input(c, 80) }} value={past} onChange={(e) => setPast(e.target.value.replace(/\D/g, '').slice(0, 4))} />
        <button style={ui.btn(c)} onClick={() => pastYear(false)}>Refresh past year once</button>
        <button style={ui.btn(c)} onClick={() => { if (window.confirm(`Refresh plan year ${past} again? It was already done once.`)) pastYear(true); }}>Again (force)</button>
        <button style={ui.btn(c)} onClick={() => { load(); if (onChanged) onChanged(); }}>Reload</button>
      </div>
      {msg && <div style={{ fontSize: fs(12), marginTop: 8 }}>{msg}</div>}
      <ui.ErrorBox msg={error} />
      {runs.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fs(12), marginTop: 10 }}>
          <thead><tr style={{ textAlign: 'left', color: c.textSecondary }}>
            <th style={{ padding: 4 }}>Started</th><th style={{ padding: 4 }}>Kind</th><th style={{ padding: 4 }}>Years</th><th style={{ padding: 4 }}>Result</th>
            <th style={{ padding: 4, textAlign: 'right' }}>Checked</th><th style={{ padding: 4, textAlign: 'right' }}>With policies</th>
            <th style={{ padding: 4, textAlign: 'right' }}>No policies</th><th style={{ padding: 4, textAlign: 'right' }}>Errors</th>
            <th style={{ padding: 4, textAlign: 'right' }}>Changes</th><th style={{ padding: 4, textAlign: 'right' }}>Calls</th>
          </tr></thead>
          <tbody>{runs.slice(0, 8).map((r) => (
            <tr key={r.id} style={{ borderTop: `1px solid ${c.borderColor}` }}>
              <td style={{ padding: 4 }}>{r.started_label}</td><td style={{ padding: 4 }}>{r.kind}</td><td style={{ padding: 4 }}>{(r.plan_years || []).join(', ')}</td>
              <td style={{ padding: 4 }} title={r.message || ''}>{r.result}{r.message ? ' *' : ''}</td>
              <td style={{ padding: 4, textAlign: 'right' }}>{r.counts?.checked ?? '—'}</td><td style={{ padding: 4, textAlign: 'right' }}>{r.counts?.with_policies ?? '—'}</td>
              <td style={{ padding: 4, textAlign: 'right' }}>{r.counts?.no_policies ?? '—'}</td><td style={{ padding: 4, textAlign: 'right' }}>{r.counts ? (r.counts.errors || 0) + (r.counts.not_found || 0) : '—'}</td>
              <td style={{ padding: 4, textAlign: 'right' }}>{r.counts?.changes ?? '—'}</td><td style={{ padding: 4, textAlign: 'right' }}>{r.counts?.calls ?? '—'}</td>
            </tr>))}
          </tbody>
        </table>
      )}
    </ui.Card>
  );
}

export function ExportsList({ c, ui, reloadKey }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { healthsherpa.policy.exports().then((r) => setRows(r.data.exports || [])).catch(() => {}); }, [reloadKey]);
  if (!rows.length) return null;
  return (
    <ui.Card c={c} title="Uploaded exports">
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fs(12) }}>
        <thead><tr style={{ textAlign: 'left', color: c.textSecondary }}>
          <th style={{ padding: 4 }}>Export of</th><th style={{ padding: 4 }}>Uploaded</th><th style={{ padding: 4 }}>By</th>
          <th style={{ padding: 4, textAlign: 'right' }}>Rows</th><th style={{ padding: 4, textAlign: 'right' }}>Rejected</th><th style={{ padding: 4, textAlign: 'right' }}>Columns</th>
        </tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id} style={{ borderTop: `1px solid ${c.borderColor}` }}>
            <td style={{ padding: 4 }}>{r.generated_label || '—'}</td><td style={{ padding: 4 }}>{r.uploaded_label}</td><td style={{ padding: 4 }}>{r.uploaded_by || '—'}</td>
            <td style={{ padding: 4, textAlign: 'right' }}>{r.rows}</td><td style={{ padding: 4, textAlign: 'right' }}>{r.rejected}</td><td style={{ padding: 4, textAlign: 'right' }}>{r.columns}</td>
          </tr>))}
        </tbody>
      </table>
    </ui.Card>
  );
}
